import { supabase } from '../../lib/supabaseClient';
import type { Database } from '../../types/database.types';
import { HEX_COLOR, type ColorOverride } from './eventColors';

export type EventType = Database['public']['Tables']['event_types']['Row'];
export type EventTypeFamily = 'hearing' | 'appointment';

export const EVENT_FAMILY_LABEL: Record<EventTypeFamily, string> = {
  hearing: 'Audiences',
  appointment: 'Rendez-vous',
};

export interface EventTypeWithUsage extends EventType {
  usage: number;
}

function checkColor(color: string): string {
  if (!HEX_COLOR.test(color)) throw new Error('Couleur invalide');
  return color.toLowerCase();
}

export async function listEventTypes(): Promise<EventType[]> {
  const { data, error } = await supabase
    .from('event_types')
    .select('*')
    .order('family')
    .order('display_order')
    .order('name');
  if (error) throw new Error(`Impossible de charger les types d'événement : ${error.message}`);
  return data ?? [];
}

/** Types avec le nombre d'audiences ou de rendez-vous qui y pointent (réglages du cabinet). */
export async function listEventTypesWithUsage(): Promise<EventTypeWithUsage[]> {
  const [types, hearings, appointments] = await Promise.all([
    listEventTypes(),
    supabase.from('dossier_hearings').select('event_type_id').not('event_type_id', 'is', null),
    supabase.from('appointments').select('event_type_id').not('event_type_id', 'is', null),
  ]);
  const usage = new Map<string, number>();
  for (const row of [...(hearings.data ?? []), ...(appointments.data ?? [])]) {
    if (row.event_type_id) usage.set(row.event_type_id, (usage.get(row.event_type_id) ?? 0) + 1);
  }
  return types.map((t) => ({ ...t, usage: usage.get(t.id) ?? 0 }));
}

export async function createEventType(input: {
  family: EventTypeFamily;
  name: string;
  color: string;
  createdBy: string | null;
}): Promise<EventType> {
  const name = input.name.trim();
  if (!name) throw new Error('Le nom du type est obligatoire');
  const { data, error } = await supabase
    .from('event_types')
    .insert({ family: input.family, name, color: checkColor(input.color), created_by: input.createdBy })
    .select()
    .single();
  if (error) {
    if (error.code === '23505') throw new Error(`« ${name} » existe déjà`);
    throw new Error(`Impossible d'ajouter le type : ${error.message}`);
  }
  return data;
}

export async function updateEventType(
  id: string,
  patch: { name?: string; color?: string; is_active?: boolean },
): Promise<void> {
  const payload: Database['public']['Tables']['event_types']['Update'] = {};
  if (patch.name !== undefined) {
    const name = patch.name.trim();
    if (!name) throw new Error('Le nom du type est obligatoire');
    payload.name = name;
  }
  if (patch.color !== undefined) payload.color = checkColor(patch.color);
  if (patch.is_active !== undefined) payload.is_active = patch.is_active;
  const { error } = await supabase.from('event_types').update(payload).eq('id', id);
  if (error) {
    if (error.code === '23505') throw new Error('Un type porte déjà ce nom');
    throw new Error(`Impossible de modifier le type : ${error.message}`);
  }
}

export async function deleteEventType(id: string): Promise<void> {
  const { error } = await supabase.from('event_types').delete().eq('id', id);
  if (error) throw new Error(`Impossible de supprimer le type : ${error.message}`);
}

// ─── Couleurs personnelles ───────────────────────────────────

export async function listMyColorOverrides(collaboratorId: string): Promise<ColorOverride[]> {
  const { data, error } = await supabase
    .from('collaborator_color_overrides')
    .select('event_type_id, task_template_id, color')
    .eq('collaborator_id', collaboratorId);
  if (error) throw new Error(`Impossible de charger vos couleurs : ${error.message}`);
  return data ?? [];
}

export type ColorTarget = { eventTypeId: string } | { taskTemplateId: string };

/** color null : retour à la couleur du cabinet. */
export async function setMyColorOverride(
  collaboratorId: string,
  target: ColorTarget,
  color: string | null,
): Promise<void> {
  const column = 'eventTypeId' in target ? 'event_type_id' : 'task_template_id';
  const targetId = 'eventTypeId' in target ? target.eventTypeId : target.taskTemplateId;

  const cleared = await supabase
    .from('collaborator_color_overrides')
    .delete()
    .eq('collaborator_id', collaboratorId)
    .eq(column, targetId);
  if (cleared.error) throw new Error(`Impossible d'enregistrer la couleur : ${cleared.error.message}`);
  if (color === null) return;

  const { error } = await supabase.from('collaborator_color_overrides').insert({
    collaborator_id: collaboratorId,
    event_type_id: column === 'event_type_id' ? targetId : null,
    task_template_id: column === 'task_template_id' ? targetId : null,
    color: checkColor(color),
  });
  if (error) throw new Error(`Impossible d'enregistrer la couleur : ${error.message}`);
}
